import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { RunnableChecklist, SyncOperation } from '@odr/shared';

/**
 * On-device store.
 *
 * This is a *cache plus an outbox*, never the system of record — SQLite on the
 * server holds the authoritative data (directive §5). Everything here is either
 * a copy the operator needs to keep working, or an operation waiting to be sent.
 */

export type QueueStatus = 'PENDING' | 'SENDING' | 'ERROR' | 'CONFLICT';

export interface QueuedOperation extends SyncOperation {
  status: QueueStatus;
  attempts: number;
  lastError?: string;
  /** Server's view when a conflict was reported, so we can explain it. */
  serverState?: unknown;
  queuedAt: string;
}

export interface PendingPhoto {
  id: string;
  runId: string;
  answerId: string | null;
  blob: Blob;
  filename: string;
  mimeType: string;
  capturedAt: string;
  /** Set once the server has accepted the upload. */
  uploaded: boolean;
}

interface OdrDb extends DBSchema {
  runs: {
    key: string;
    value: RunnableChecklist & { cachedAt: string };
  };
  queue: {
    key: string;
    value: QueuedOperation;
    indexes: { 'by-status': QueueStatus; 'by-queuedAt': string };
  };
  photos: {
    key: string;
    value: PendingPhoto;
    indexes: { 'by-run': string; 'by-uploaded': number };
  };
  meta: {
    key: string;
    value: unknown;
  };
}

let dbPromise: Promise<IDBPDatabase<OdrDb>> | null = null;

export function db(): Promise<IDBPDatabase<OdrDb>> {
  dbPromise ??= openDB<OdrDb>('odr', 1, {
    upgrade(database) {
      database.createObjectStore('runs', { keyPath: 'run.id' });

      const queue = database.createObjectStore('queue', { keyPath: 'operationId' });
      queue.createIndex('by-status', 'status');
      queue.createIndex('by-queuedAt', 'queuedAt');

      const photos = database.createObjectStore('photos', { keyPath: 'id' });
      photos.createIndex('by-run', 'runId');
      photos.createIndex('by-uploaded', 'uploaded');

      database.createObjectStore('meta');
    },
  });
  return dbPromise;
}

// --- Cached runs -----------------------------------------------------------

export async function cacheRun(run: RunnableChecklist): Promise<void> {
  const database = await db();
  await database.put('runs', { ...run, cachedAt: new Date().toISOString() });
}

export async function getCachedRun(runId: string): Promise<RunnableChecklist | undefined> {
  const database = await db();
  return database.get('runs', runId);
}

export async function listCachedRuns(): Promise<RunnableChecklist[]> {
  const database = await db();
  return database.getAll('runs');
}

export async function dropCachedRun(runId: string): Promise<void> {
  const database = await db();
  await database.delete('runs', runId);
}

// --- Outbox ----------------------------------------------------------------

export async function enqueue(operation: SyncOperation): Promise<void> {
  const database = await db();
  await database.put('queue', {
    ...operation,
    status: 'PENDING',
    attempts: 0,
    queuedAt: new Date().toISOString(),
  });
}

export async function queuedOperations(): Promise<QueuedOperation[]> {
  const database = await db();
  const all = await database.getAll('queue');
  // Order matters: run.start must reach the server before run.answer.
  return all.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

export async function pendingOperations(): Promise<QueuedOperation[]> {
  return (await queuedOperations()).filter(
    (op) => op.status === 'PENDING' || op.status === 'ERROR',
  );
}

export async function updateOperation(
  operationId: string,
  patch: Partial<QueuedOperation>,
): Promise<void> {
  const database = await db();
  const existing = await database.get('queue', operationId);
  if (!existing) return;
  await database.put('queue', { ...existing, ...patch });
}

export async function removeOperation(operationId: string): Promise<void> {
  const database = await db();
  await database.delete('queue', operationId);
}

export async function countByStatus(): Promise<Record<QueueStatus, number>> {
  const all = await queuedOperations();
  return {
    PENDING: all.filter((o) => o.status === 'PENDING').length,
    SENDING: all.filter((o) => o.status === 'SENDING').length,
    ERROR: all.filter((o) => o.status === 'ERROR').length,
    CONFLICT: all.filter((o) => o.status === 'CONFLICT').length,
  };
}

// --- Photos ----------------------------------------------------------------

export async function stashPhoto(photo: Omit<PendingPhoto, 'uploaded'>): Promise<void> {
  const database = await db();
  await database.put('photos', { ...photo, uploaded: false });
}

export async function pendingPhotos(): Promise<PendingPhoto[]> {
  const database = await db();
  const all = await database.getAll('photos');
  return all.filter((p) => !p.uploaded);
}

export async function photosForRun(runId: string): Promise<PendingPhoto[]> {
  const database = await db();
  return database.getAllFromIndex('photos', 'by-run', runId);
}

export async function markPhotoUploaded(id: string): Promise<void> {
  const database = await db();
  const existing = await database.get('photos', id);
  if (existing) await database.put('photos', { ...existing, uploaded: true });
}

export async function dropPhoto(id: string): Promise<void> {
  const database = await db();
  await database.delete('photos', id);
}

// --- Meta ------------------------------------------------------------------

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const database = await db();
  return (await database.get('meta', key)) as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const database = await db();
  await database.put('meta', value, key);
}

/** Stable per-device id, used to attribute sync operations in the server log. */
export async function clientId(): Promise<string> {
  const existing = await getMeta<string>('clientId');
  if (existing) return existing;
  const generated = `web-${crypto.randomUUID()}`;
  await setMeta('clientId', generated);
  return generated;
}

/** Wipe local state on sign-out so a shared tablet never leaks between operators. */
export async function clearAll(): Promise<void> {
  const database = await db();
  await Promise.all([
    database.clear('runs'),
    database.clear('queue'),
    database.clear('photos'),
  ]);
}
